/** @jsxImportSource @emotion/react */
import { useCallback, useMemo, useState } from 'react'
import Typography from '@mui/material/Typography'
import { ActiveElement, Chart as ChartJs, ChartEvent, registerables } from 'chart.js'
import { Doughnut } from 'react-chartjs-2'
import { CANCELLATION_REASON_COLORS } from '../constants'
import { CancellationReasonStat } from '../types'
import { getPercentage } from '../utils'
import * as classes from '../styles'

ChartJs.register(...registerables)

type Props = {
  reasons: CancellationReasonStat[]
  totalCancellations: number
  loading: boolean
  hasError: boolean
}

export const CancellationReasonsSummary = ({ reasons, totalCancellations, loading, hasError }: Props) => {
  const [hoveredSegmentIndex, setHoveredSegmentIndex] = useState<number | null>(null)

  const handleChartHover = useCallback((_: ChartEvent, elements: ActiveElement[]) => {
    setHoveredSegmentIndex(elements.length ? elements[0].index : null)
  }, [])

  const chartData = useMemo(
    () => ({
      labels: reasons.map(({ reason }) => reason),
      datasets: [
        {
          data: reasons.map(({ count }) => count),
          backgroundColor: reasons.map(
            (_, index) => CANCELLATION_REASON_COLORS[index % CANCELLATION_REASON_COLORS.length]
          ),
          borderWidth: 3,
          borderRadius: 5,
          offset: 2,
          hoverBorderColor: 'transparent',
        },
      ],
    }),
    [reasons]
  )

  const chartOptions = useMemo(
    () => ({
      cutout: '60%',
      plugins: {
        tooltip: {
          enabled: false,
        },
        legend: {
          display: false,
        },
      },
      onHover: handleChartHover,
    }),
    [handleChartHover]
  )

  return (
    <div css={classes.analyticsCard}>
      <div css={classes.chartColumn}>
        <Typography variant="title4">Cancellation Reasons</Typography>

        {loading && !reasons.length ? (
          <div css={classes.emptyChart}>Loading cancellation stats...</div>
        ) : hasError ? (
          <div css={classes.emptyChart}>Failed to load cancellation stats.</div>
        ) : totalCancellations ? (
          <div css={classes.chartWrapper}>
            <Doughnut data={chartData} options={chartOptions} />
            <div css={classes.centerText}>
              <Typography css={classes.totalValue}>{totalCancellations}</Typography>
              <Typography css={classes.totalLabel}>Cancellations</Typography>
            </div>
          </div>
        ) : (
          <div css={classes.emptyChart}>No cancellations found for this period.</div>
        )}
      </div>

      <ul css={classes.reasonsList}>
        {reasons.map(({ reason, count }, index) => (
          <li key={reason} css={classes.reasonRow}>
            <div css={classes.reasonName}>
              <div css={classes.dotWrapper}>
                <div
                  css={classes.dot(
                    CANCELLATION_REASON_COLORS[index % CANCELLATION_REASON_COLORS.length],
                    index === hoveredSegmentIndex
                  )}
                />
              </div>
              <Typography css={classes.reasonText}>{reason}</Typography>
            </div>
            <Typography css={classes.reasonCount}>{count}</Typography>
            <Typography css={classes.reasonPercent}>{getPercentage(count, totalCancellations)}</Typography>
          </li>
        ))}
      </ul>
    </div>
  )
}
